import React, { Fragment, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useHistory, useParams } from "react-router-dom";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderRow,
    DataTableOverflowMenuCell,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    List,
    ListItem,
    TextButton,
    Typography,
} from "@episerver/ui-framework";
import { EnumData } from "../model/Enum";
import {
    enumsSelector,
    enumListTitle,
    onChangeEnumPage,
    onChangeEnumRowPerPage,
    paginationSelector,
    confirmDialogSelector,
    onChangeConfirmDialog,
    initialEnumState,
} from "../reducers/enumManagerReducer";
import { deleteEnum, fetchEnumsByType } from "../reducers/asyncThunks";
import { enumFormTitle, initialEnumFormState, onOpenDialog } from "../reducers/enumFormReducer";

const EnumList = () => {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { from, detail } = (history.location.state as any) || { from: "", detail: null };

    const { type } = useParams<{ type: string }>();
    const enums = useAppSelector(enumsSelector);
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useAppSelector(paginationSelector);
    const confirmDialog = useAppSelector(confirmDialogSelector);
    useEffect(() => {
        dispatch(fetchEnumsByType(type));
    }, []);

    useEffect(() => {
        if (history.location.state != null) {
            window.history.replaceState(history.location.state, "");
        }
    }, [history.location.state]);

    const columns: Array<{ selector: string; title: string }> = [
        { selector: "", title: "No" },
        { selector: "name", title: "Item Value" },
        { selector: "name", title: "Display Value" },
        { selector: "", title: "" },
    ];

    const onConfirmDialog = (enumData: EnumData, message: string): void => {
        dispatch(onChangeConfirmDialog({ ...confirmDialog, open: true, enum: enumData, message: message }));
    };

    const onDelete = (): void => {
        dispatch(deleteEnum(confirmDialog.enum));
        dispatch(onChangeConfirmDialog(initialEnumState.confirmDialog));
    };

    const onCancel = (): void => {
        dispatch(onChangeConfirmDialog(initialEnumState.confirmDialog));
    };

    const renderRow = (rowData: EnumData, index: number): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.id.toString()} key={rowData.id}>
                <DataTableCell>{index + 1}</DataTableCell>
                <DataTableCell>{rowData.name}</DataTableCell>
                <DataTableCell>{rowData.name}</DataTableCell>
                <DataTableOverflowMenuCell>
                    <List>
                        <ListItem
                            onClick={() =>
                                dispatch(
                                    onOpenDialog({
                                        form: {
                                            open: true,
                                            title: `Edit ${enumFormTitle[type]}`,
                                            enumData: rowData,
                                        },
                                    })
                                )
                            }
                        >
                            Edit
                        </ListItem>
                        <ListItem onClick={() => onConfirmDialog(rowData, `Are you sure to delete "${rowData.name}"?`)}>
                            Delete
                        </ListItem>
                    </List>
                </DataTableOverflowMenuCell>
            </DataTableRow>
        );
    };

    return (
        <Fragment>
            <Typography use="headline1">Manage {enumListTitle[type]}</Typography>
            <br />
            <DataTable>
                <DataTableToolbar>
                    <TextButton
                        onClick={() =>
                            dispatch(
                                onOpenDialog({
                                    form: {
                                        open: true,
                                        title: `Create ${enumFormTitle[type]}`,
                                        enumData: initialEnumFormState.form.enumData,
                                    },
                                })
                            )
                        }
                    >
                        Create
                    </TextButton>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <React.Fragment>
                            {columns.map((column, index) => {
                                return (
                                    <DataTableColumnHeaderCell key={index}>{column.title}</DataTableColumnHeaderCell>
                                );
                            })}
                        </React.Fragment>
                    </DataTableHeaderRow>
                    <DataTableBody>
                        {enums.map((row: EnumData, index: number) => {
                            if (index < startRow || index >= startRow + rowsPerPage) {
                                return <React.Fragment key={row.id}></React.Fragment>;
                            }
                            return renderRow(row, index);
                        })}
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={(startRow) => dispatch(onChangeEnumPage(startRow))}
                    onChangeRowsPerPage={(rowsPerPage) => dispatch(onChangeEnumRowPerPage(rowsPerPage))}
                />
            </DataTable>
            <br />
            <br />
            <div className="edit-form-buttons">
                <TextButton
                    onClick={() =>
                        history.push({
                            pathname: from === "" ? window.history.state.from : from,
                            state: { from: history.location.pathname, detail: detail ?? window.history.state.detail },
                        })
                    }
                >
                    Back
                </TextButton>
            </div>
            <Dialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                dismissLabel="Cancel"
                confirmLabel="Delete"
                enableConfirm
                onInteraction={(e) => {
                    if (e.detail.action === "confirm") onDelete();
                    else onCancel();
                }}
            >
                <React.Fragment>
                    <p>{confirmDialog.message}</p>
                </React.Fragment>
            </Dialog>
        </Fragment>
    );
};

export default EnumList;
